<script lang="ts">
  import { browser } from '$app/environment';
  import { onMount, onDestroy } from 'svelte';
    import { TooltipTextAlignment } from './CTooltipProp';

  const ClassName = 'ctooltip';
  const ArrowClassName = 'ctooltip-arrow';
  const ThresholdValue = 50; // in pixel
  const TooltipArrowSpace = 10; // in pixel

  enum Position {
    Top,
    Left,
    Bottom,
    Right
  }

  const MapClassWithPosition: Record<Position, string> = {
    [Position.Top]: 'top',
    [Position.Left]: 'left',
    [Position.Bottom]: 'bottom',
    [Position.Right]: 'right'
  };

  interface Props {
    label: string;
    width?: string;
    hide?: boolean;
    textAlign?: TooltipTextAlignment;
    children?: import('svelte').Snippet;
  }

  let {
    label,
    width = '',
    hide = false,
    textAlign = TooltipTextAlignment.Inherit,
    children
  }: Props = $props();

  let tooltipElement: HTMLElement;

  onMount(() => {
    mountTooltip();
  });

  onDestroy(() => {
    resetTooltip();
  });

  const mountTooltip = (): void => {
    if (browser && !hide) {
      tooltipElement = document.querySelector(`.${ClassName}`);
      if (!tooltipElement) {
        tooltipElement = document.createElement('div');
        tooltipElement.classList.add(ClassName);
        document.body.append(tooltipElement);
      }
    }
  };

  const onMouseOver = (e: any): void => {
    if (!tooltipElement) return;

    tooltipElement.innerHTML = label;
    tooltipElement.style.display = 'block';
    width && (tooltipElement.style.width = width);
    textAlign && (tooltipElement.style.textAlign = textAlign);

    const dim: DOMRect = e.target.getBoundingClientRect();
    const { x, y, position } = calculatePosition(dim);
    tooltipElement.style.left = `${x}px`;
    tooltipElement.style.top = `${y}px`;

    // For Tooltip arrow
    const childDiv = document.createElement('div');
    childDiv.classList.add(ArrowClassName, MapClassWithPosition[position]);
    tooltipElement.appendChild(childDiv);
  };

  const calculatePosition = (dimension: DOMRect): { x: number; y: number; position: Position } => {
    const position = findPosition(dimension);

    let x = 0;
    let y = 0;
    const tooltipDim: DOMRect = tooltipElement.getBoundingClientRect();
    // Target element center pixel
    const targetCenterWidth = dimension.width / 2;
    const targetCenterHeight = dimension.height / 2;
    // Tool tip center pixel
    const tooltipCenterWidth = tooltipDim.width / 2;
    const tooltipCenterHeight = tooltipDim.height / 2;
    switch (position) {
      case Position.Top: {
        x = dimension.x + targetCenterWidth - tooltipCenterWidth;
        y = dimension.y - tooltipDim.height - TooltipArrowSpace;
        break;
      }
      case Position.Left: {
        x = dimension.x - tooltipDim.width - TooltipArrowSpace;
        y = dimension.y + targetCenterHeight - tooltipCenterHeight;
        break;
      }
      case Position.Bottom: {
        x = dimension.x + targetCenterWidth - tooltipCenterWidth;
        y = dimension.y + dimension.height + TooltipArrowSpace;
        break;
      }
      case Position.Right: {
        x = dimension.x + dimension.width + TooltipArrowSpace;
        y = dimension.y + targetCenterHeight - tooltipCenterHeight;
        break;
      }
    }
    return { x, y, position };
  };

  const findPosition = (dimension: DOMRect): Position => {
    const { top, height, left, right, width, bottom } = dimension;

    if (!browser) return;
    const windowWidth = window.innerWidth - ThresholdValue;
    const windowHeight = window.innerHeight - ThresholdValue;

    const tooltipDim: DOMRect = tooltipElement.getBoundingClientRect();
    const isRightOverflow = dimension.x + tooltipDim.width > windowWidth;
    const isLeftOverflow = dimension.x - tooltipDim.width < ThresholdValue;
    // For Top
    const isTop = top - height > ThresholdValue && !isRightOverflow && !isLeftOverflow;
    if (isTop) return Position.Top;

    // For Left
    const isLeft = left - width > ThresholdValue;
    if (isLeft) return Position.Left;

    // For Bottom
    const isBottom = bottom + height < windowHeight && !isRightOverflow && !isLeftOverflow;
    if (isBottom) return Position.Bottom;

    // For Right
    const isRight = right + width < windowWidth;
    if (isRight) return Position.Right;
  };

  const resetTooltip = (): void => {
    if (!tooltipElement) return;

    tooltipElement.innerHTML = '';
    tooltipElement.style.top = `-1000px`;
    tooltipElement.style.left = `-1000px`;
    tooltipElement.style.display = 'none';
    tooltipElement.style.width = '250px';
  };

  const onMouseOut = (): void => {
    resetTooltip();
  };
</script>

<!-- svelte-ignore a11y_mouse_events_have_key_events -->
{#if hide}
  {@render children?.()}
{:else}
  <div class="comp-ctooltip" onmouseover={onMouseOver} onmouseout={onMouseOut}>
    {@render children?.()}
  </div>
{/if}

<style>
  .comp-ctooltip {
    position: relative;
    display: inline-block;
  }
  :global(.ctooltip) {
    width: 250px;
    z-index: 9999999;
    top: -1000px;
    left: -1000px;
    color: #585c64;
    padding: 10px;
    border-radius: 3px;
    position: fixed;
    box-shadow:
      0 4px 18px 3px rgb(0 0 0 / 10%),
      0 3px 11px 1px rgb(0 0 0 / 8%);
    background: white;
    display: none;
  }
  :global(.ctooltip-arrow) {
    position: absolute;
    border-width: 5px;
    border-style: solid;
    border-color: white;
    transform: translate(-50%, -50%) rotate(45deg);
  }
  :global(.ctooltip-arrow.top) {
    top: 100%;
    left: 50%;
  }
  :global(.ctooltip-arrow.left) {
    top: 50%;
    left: 100%;
  }
  :global(.ctooltip-arrow.bottom) {
    bottom: 100%;
    left: 50%;
    transform: translate(-50%, 50%) rotate(45deg);
  }
  :global(.ctooltip-arrow.right) {
    right: 100%;
    top: 50%;
    transform: translate(50%, 0%) rotate(45deg);
  }
</style>
